// filepath: /home/toofoocustomer/tf-node/tf_customer_web/components/customer/ForgotPasswordForm.tsx

"use client";

import Link from "next/link";
import { FormEvent, useState } from "react";

import {
  customerIdentityErrorMessage,
  requestCustomerPasswordReset,
} from "@/lib/customer/identity-api";

type Props = {
  resume?: string;
};

function withResume(pathname: string, resume?: string): string {
  if (!resume) return pathname;
  const query = new URLSearchParams({ resume });
  return `${pathname}?${query.toString()}`;
}

export function ForgotPasswordForm({ resume }: Props) {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage("");
    setSubmitting(true);

    try {
      await requestCustomerPasswordReset(email.trim());
      setSubmitted(true);
    } catch (error) {
      setErrorMessage(customerIdentityErrorMessage(error, "forgot"));
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="status-card embedded-status" data-e2e="customer-forgot-password-accepted">
        <div className="status-mark">✓</div>
        <h1>이메일을 확인해 주세요</h1>
        <p>
          입력한 이메일로 사용할 수 있는 TOOFOO 계정이 있다면 비밀번호 재설정 링크를 보냈습니다.
        </p>
        {resume ? (
          <p className="field-help">비밀번호를 변경한 뒤 이 화면으로 돌아와 서비스 로그인을 계속할 수 있습니다.</p>
        ) : null}
        <div className="status-actions">
          <Link className="secondary-button link-button" href={withResume("/login", resume)}>
            {resume ? "서비스 로그인으로 돌아가기" : "로그인으로 돌아가기"}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form className="auth-form" onSubmit={submit} data-e2e="customer-forgot-password-form">
      <div className="form-field">
        <label htmlFor="email">이메일</label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="name@example.com"
          required
          maxLength={320}
        />
        <p className="field-help">가입할 때 사용한 이메일을 입력해 주세요.</p>
      </div>

      {errorMessage ? (
        <div className="form-error" role="alert" data-e2e="customer-forgot-password-error">
          {errorMessage}
        </div>
      ) : null}

      <button className="primary-button" type="submit" disabled={submitting}>
        {submitting ? "요청 중..." : "재설정 링크 받기"}
      </button>

      <p className="auth-switch">
        비밀번호가 기억나시나요? <Link href={withResume("/login", resume)}>로그인</Link>
      </p>
    </form>
  );
}
